import { useCallback } from 'react';

import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { useShowSuggestionsPanel } from '../useShowSuggestionsPanel.ts';

/**
 * Returns the Review action that opens the Suggestions panel on a published page, or undefined when it isn't available.
 *
 * @param contentId - The published content id. Pass undefined when the caller isn't eligible for Review.
 * @param contentType - The content type, used to check edit permission.
 */
export const usePublishedPageReviewAction = (
	contentId: string | undefined,
	contentType: string | null | undefined,
): (() => void) | undefined => {
	const canUpdateContent = useCanPerformContentOperation({
		contentId: contentId ?? '',
		contentType: contentType === 'blogpost' ? 'blogpost' : 'page',
		operation: OperationTypes.UPDATE,
	});
	const { showSuggestionsPanel } = useShowSuggestionsPanel();
	const showPublishedSuggestionsPanel = useCallback(
		() => showSuggestionsPanel({ publishedContentId: contentId }),
		[contentId, showSuggestionsPanel],
	);

	if (
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- The suggested edits experiment is the kill switch for the view mode Suggestions panel subgate.
		!contentId ||
		!canUpdateContent ||
		!expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) ||
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- View mode Suggestions panel subgate.
		!fg('confluence_suggestions_panel_view_mode')
	) {
		return undefined;
	}
	return showPublishedSuggestionsPanel;
};
